import * as THREE from "three";
import { cheapTextures } from "./mapTextures.js";

/**
 * Procedural textures for COMBAT FX — explosion fire, smoke, embers, muzzle
 * flash, blast-ring shockwaves and charred debris. Canvas-generated (no asset
 * files), module-cached, one GPU upload each, built lazily and never rebuilt.
 *
 * Why these exist (mission `graphics`, 2026-08-01). Every other entity family in
 * the game has now had a surfacing pass — maps, avatars, weapons, vehicles,
 * monsters, beasts, props, the sky. Combat FX were the last family still drawn
 * entirely from UNTEXTURED flat-colour primitives:
 *
 *   - an explosion was ONE additive `SphereGeometry(1,16,16)` scaled up. A
 *     constant-colour sphere has a perfectly smooth, perfectly circular
 *     silhouette — at any size it reads as a expanding balloon, not as burning
 *     gas. The "smoke" was a second sphere of flat `#3a3330`.
 *   - a spark burst was n untextured `PlaneGeometry(1,1)` quads facing world +Z,
 *     i.e. square confetti that turns edge-on as you strafe.
 *   - the muzzle flash was ONE untextured 0.14 m plane with a random Z roll.
 *   - mega debris chunks were flat `#ff6a2a` boxes.
 *
 * The fix is two-sided and both halves are needed: these textures supply the
 * internal structure and the chewed silhouette, and `worldEntities.ts` supplies
 * the VOLUME — a blast is now a cloud of camera-facing puffs with their own
 * drag, lift and rotation instead of one sphere.
 *
 * Budget (full / `cheapTextures()`): fire 256²/128², smoke 256²/128²,
 * ember 64², flash 128²/64², ring 128²/64², debris 64². ~0.6 MB pre-mip full,
 * ~0.25 MB cheap. All six built once per tab.
 */

/** Deterministic hash noise — see the same helper in fxTextures.ts. A fixed
 *  pattern keeps an A/B screenshot reproducible. */
function rnd(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function cvs(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  return { c, g: c.getContext("2d")! };
}

/** Value noise on an integer lattice, smoothstep-interpolated. Not tiled — none
 *  of these textures repeat, they are all single billboards. */
function noiseField(S: number, cells: number, seed: number): Float32Array {
  const r = rnd(seed);
  const g = new Float32Array((cells + 1) * (cells + 1));
  for (let i = 0; i < g.length; i++) g[i] = r();
  const out = new Float32Array(S * S);
  const sm = (t: number): number => t * t * (3 - 2 * t);
  for (let y = 0; y < S; y++) {
    const fy = (y / S) * cells, gy = Math.floor(fy), ty = sm(fy - gy);
    for (let x = 0; x < S; x++) {
      const fx = (x / S) * cells, gx = Math.floor(fx), tx = sm(fx - gx);
      const a = g[gy * (cells + 1) + gx], b = g[gy * (cells + 1) + gx + 1];
      const c = g[(gy + 1) * (cells + 1) + gx], d = g[(gy + 1) * (cells + 1) + gx + 1];
      out[y * S + x] = (a + (b - a) * tx) + ((c + (d - c) * tx) - (a + (b - a) * tx)) * ty;
    }
  }
  return out;
}

/** Sum of octaves of `noiseField`, normalised to roughly 0..1. */
function fbm(S: number, base: number, oct: number, seed: number): Float32Array {
  const out = new Float32Array(S * S);
  let amp = 1, norm = 0;
  for (let o = 0; o < oct; o++) {
    const n = noiseField(S, base << o, seed + o * 7919);
    for (let i = 0; i < out.length; i++) out[i] += n[i] * amp;
    norm += amp;
    amp *= 0.5;
  }
  for (let i = 0; i < out.length; i++) out[i] /= norm;
  return out;
}

let fireTex: THREE.CanvasTexture | null = null;

/**
 * ONE FIREBALL PUFF, additive. RGB is a blackbody-ish ramp (white-hot core ->
 * yellow -> orange -> deep red) driven by how deep into the puff the texel sits;
 * A is a radial falloff WARPED by a 4-octave fBm so the rim breaks into
 * cauliflower lobes instead of ending on a circle.
 *
 * The soot band matters as much as the fire: the outermost ~15% of the puff
 * ramps its RGB DOWN toward a dark ember red while alpha is still non-zero, so
 * an additive quad adds almost nothing there and the puff visually dissolves
 * into the smoke behind it. That gradient is what stops n stacked puffs reading
 * as one flat orange blob.
 */
export function firePuffTexture(): THREE.CanvasTexture {
  if (fireTex) return fireTex;
  const S = cheapTextures() ? 128 : 256;
  const { c, g } = cvs(S, S);
  const warp = fbm(S, 3, 4, 0x1f00d);
  const detail = fbm(S, 7, 3, 0x77c1e);
  const img = g.createImageData(S, S);
  const d = img.data;
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const dx = (x - cx) / (S / 2), dy = (y - cy) / (S / 2);
      const rad = Math.hypot(dx, dy);
      // lobed radius: the fBm pushes the effective edge in and out by ±30%
      const lobe = 0.72 + (warp[i] - 0.5) * 0.86;
      // `dens` is 1 at the core, 0 at the lobed rim
      let dens = 1 - rad / Math.max(0.08, lobe);
      dens -= (1 - detail[i]) * 0.28; // fine boiling structure
      // the heart of the puff is combusting, not just thick — without this bump
      // the boiling term keeps the centre off the top of the heat ramp and the
      // whole puff sits at "orange", never reaching white
      dens += Math.max(0, 1 - rad / 0.5) * 0.42;
      dens = Math.max(0, Math.min(1, dens));
      const o = i * 4;
      if (dens <= 0) { d[o] = d[o + 1] = d[o + 2] = 0; d[o + 3] = 0; continue; }
      // heat ramp — the core is genuinely white, then yellow, orange, red, soot
      const h = Math.pow(dens, 0.9);
      let R: number, G: number, B: number;
      if (h > 0.78) { const t = (h - 0.78) / 0.22; R = 255; G = 215 + 40 * t; B = 150 + 105 * t; }
      else if (h > 0.5) { const t = (h - 0.5) / 0.28; R = 255; G = 130 + 85 * t; B = 30 + 120 * t; }
      else if (h > 0.22) { const t = (h - 0.22) / 0.28; R = 226 + 29 * t; G = 56 + 74 * t; B = 12 + 18 * t; }
      else { const t = h / 0.22; R = 70 + 156 * t; G = 20 + 36 * t; B = 14 - 2 * t; }
      d[o] = R; d[o + 1] = G; d[o + 2] = B;
      // alpha holds up further out than the colour does, so the soot rim exists
      d[o + 3] = Math.round(255 * Math.min(1, Math.pow(dens, 0.45) * 1.35));
    }
  }
  g.putImageData(img, 0, 0);
  fireTex = new THREE.CanvasTexture(c);
  fireTex.colorSpace = THREE.SRGBColorSpace;
  return fireTex;
}

let smokeTex: THREE.CanvasTexture | null = null;

/**
 * ONE SMOKE PUFF, normal-blended. A is a billowing fBm-warped falloff; RGB is
 * BAKED LIGHTING, not a flat grey — the alpha field is treated as a height
 * field, Sobel'd, and lit from the upper left, so the puff has lobes that catch
 * light on one side and fall into shadow on the other.
 *
 * That bake is the whole point. These are `MeshBasicMaterial` billboards (no
 * lighting, by design — they must not cost a lit draw), so without a baked
 * gradient a smoke puff is a flat grey disc and ten of them are a flat grey
 * disc. With it, a cloud of puffs reads as a churning volume.
 */
export function smokePuffTexture(): THREE.CanvasTexture {
  if (smokeTex) return smokeTex;
  const S = cheapTextures() ? 128 : 256;
  const { c, g } = cvs(S, S);
  const warp = fbm(S, 3, 5, 0x5ac07);
  const img = g.createImageData(S, S);
  const d = img.data;
  const cx = (S - 1) / 2, cy = (S - 1) / 2;
  const a = new Float32Array(S * S);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const rad = Math.hypot((x - cx) / (S / 2), (y - cy) / (S / 2));
      const lobe = 0.74 + (warp[i] - 0.5) * 0.5;
      const v = 1 - rad / Math.max(0.08, lobe);
      a[i] = Math.max(0, Math.min(1, v)) * (0.55 + warp[i] * 0.6);
    }
  }
  const at = (x: number, y: number): number =>
    a[Math.min(S - 1, Math.max(0, y)) * S + Math.min(S - 1, Math.max(0, x))];
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const i = y * S + x;
      const o = i * 4;
      const al = Math.min(1, a[i] * 1.25);
      if (al <= 0.004) { d[o] = d[o + 1] = d[o + 2] = 0; d[o + 3] = 0; continue; }
      // Sobel the density: the gradient of a billow IS its surface slope
      const gx = at(x + 2, y) - at(x - 2, y);
      const gy = at(x, y + 2) - at(x, y - 2);
      // light from upper-left-front (-x, -y in texture space, +z out of screen)
      let nx = -gx * 10, ny = -gy * 10, nz = 1;
      const l = Math.hypot(nx, ny, nz); nx /= l; ny /= l; nz /= l;
      const lambert = Math.max(0, nx * -0.55 + ny * -0.62 + nz * 0.56);
      // ambient floor + directional term, times a low-frequency occlusion from
      // the same billow field (deep folds are darker than exposed ones), plus a
      // slight warm tint in the lit lobes — a fireball lights its own smoke
      const lum = Math.min(1,
        (0.12 + 0.9 * Math.pow(lambert, 0.8)) * (0.62 + 0.62 * warp[i]));
      d[o] = Math.round(255 * Math.min(1, lum * 1.04));
      d[o + 1] = Math.round(255 * lum);
      d[o + 2] = Math.round(255 * lum * 0.95);
      d[o + 3] = Math.round(255 * al);
    }
  }
  g.putImageData(img, 0, 0);
  smokeTex = new THREE.CanvasTexture(c);
  smokeTex.colorSpace = THREE.SRGBColorSpace;
  return smokeTex;
}

let emberTex: THREE.CanvasTexture | null = null;

/**
 * A single EMBER: hot core near the top of the quad with a soft glow and a
 * tapering TAIL running down -V. `worldEntities.spawnBurst` orients the quad's
 * +Y along the spark's velocity and stretches it by speed, so the tail always
 * trails behind the head — a motion-blur streak for free, which is what makes a
 * fast spark read as a spark rather than as a square.
 */
export function emberTexture(): THREE.CanvasTexture {
  if (emberTex) return emberTex;
  const S = 64;
  const { c, g } = cvs(S, S);
  g.clearRect(0, 0, S, S);
  const headY = S * 0.3;
  // the tail first, so the head's glow sits on top of it
  const tail = g.createLinearGradient(0, headY, 0, S * 0.98);
  tail.addColorStop(0, "rgba(255,236,190,0.85)");
  tail.addColorStop(0.45, "rgba(255,150,60,0.32)");
  tail.addColorStop(1, "rgba(180,50,20,0)");
  g.fillStyle = tail;
  g.beginPath();
  g.moveTo(S * 0.5 - S * 0.105, headY);
  g.lineTo(S * 0.5 + S * 0.105, headY);
  g.lineTo(S * 0.5 + S * 0.012, S * 0.98);
  g.lineTo(S * 0.5 - S * 0.012, S * 0.98);
  g.closePath();
  g.fill();
  const glow = g.createRadialGradient(S * 0.5, headY, 0, S * 0.5, headY, S * 0.3);
  glow.addColorStop(0, "rgba(255,255,255,1)");
  glow.addColorStop(0.22, "rgba(255,232,170,0.92)");
  glow.addColorStop(0.55, "rgba(255,140,50,0.34)");
  glow.addColorStop(1, "rgba(255,90,20,0)");
  g.globalCompositeOperation = "lighter";
  g.fillStyle = glow;
  g.fillRect(0, 0, S, S);
  g.globalCompositeOperation = "source-over";
  emberTex = new THREE.CanvasTexture(c);
  emberTex.colorSpace = THREE.SRGBColorSpace;
  return emberTex;
}

let flashTex: THREE.CanvasTexture | null = null;

/**
 * MUZZLE FLASH: an irregular star of 9 unequal petals over a white-hot core,
 * with a ring of fine sparks. Additive.
 *
 * The old flash was a plain 0.14 m square with a solid colour — at the centre of
 * the screen, 75 ms at a time, that reads as a glowing postage stamp. Real
 * muzzle flash is asymmetric and spiky, and the asymmetry is what sells the
 * randomised roll the viewmodel already applies (rolling a SQUARE is invisible).
 */
export function muzzleFlashTexture(): THREE.CanvasTexture {
  if (flashTex) return flashTex;
  const S = cheapTextures() ? 64 : 128;
  const { c, g } = cvs(S, S);
  const r = rnd(0xf1a58);
  const cx = S / 2, cy = S / 2;
  g.globalCompositeOperation = "lighter";
  // petals — long/short alternating, each a tapering triangle with a soft tip
  const P = 9;
  for (let i = 0; i < P; i++) {
    const ang = (i / P) * Math.PI * 2 + r() * 0.25;
    const len = S * (i % 2 === 0 ? 0.30 + r() * 0.18 : 0.14 + r() * 0.12);
    const w = S * (0.035 + r() * 0.05);
    const grad = g.createLinearGradient(cx, cy, cx + Math.cos(ang) * len, cy + Math.sin(ang) * len);
    grad.addColorStop(0, "rgba(255,246,214,0.95)");
    grad.addColorStop(0.4, "rgba(255,186,70,0.55)");
    grad.addColorStop(1, "rgba(255,110,26,0)");
    g.fillStyle = grad;
    const px = Math.cos(ang + Math.PI / 2) * w, py = Math.sin(ang + Math.PI / 2) * w;
    g.beginPath();
    g.moveTo(cx + px, cy + py);
    g.lineTo(cx + Math.cos(ang) * len, cy + Math.sin(ang) * len);
    g.lineTo(cx - px, cy - py);
    g.closePath();
    g.fill();
  }
  // fine sparks flung past the petals
  for (let i = 0; i < 22; i++) {
    const ang = r() * Math.PI * 2;
    const d0 = S * (0.16 + r() * 0.1), d1 = d0 + S * (0.04 + r() * 0.16);
    g.strokeStyle = `rgba(255,${(190 + r() * 60) | 0},${(90 + r() * 90) | 0},${(0.18 + r() * 0.4).toFixed(3)})`;
    g.lineWidth = 0.7 + r() * 1.4;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(cx + Math.cos(ang) * d0, cy + Math.sin(ang) * d0);
    g.lineTo(cx + Math.cos(ang) * d1, cy + Math.sin(ang) * d1);
    g.stroke();
  }
  const core = g.createRadialGradient(cx, cy, 0, cx, cy, S * 0.22);
  core.addColorStop(0, "rgba(255,255,255,1)");
  core.addColorStop(0.35, "rgba(255,240,190,0.85)");
  core.addColorStop(0.7, "rgba(255,170,60,0.30)");
  core.addColorStop(1, "rgba(255,120,30,0)");
  g.fillStyle = core;
  g.fillRect(0, 0, S, S);
  g.globalCompositeOperation = "source-over";
  flashTex = new THREE.CanvasTexture(c);
  flashTex.colorSpace = THREE.SRGBColorSpace;
  return flashTex;
}

let ringTex: THREE.CanvasTexture | null = null;

/**
 * The SHOCKWAVE / kill-aura ring surface: radial streaks that thin out toward
 * the leading edge, on a soft-edged annulus. `RingGeometry`'s default UVs are
 * planar over the bounding box, so a centred radial pattern lands correctly.
 * Turns a hard-edged flat torus into a dust front with grain in it.
 */
export function shockRingTexture(): THREE.CanvasTexture {
  if (ringTex) return ringTex;
  const S = cheapTextures() ? 64 : 128;
  const { c, g } = cvs(S, S);
  const r = rnd(0x33c19);
  const cx = S / 2, cy = S / 2;
  g.globalCompositeOperation = "lighter";
  for (let i = 0; i < 96; i++) {
    const ang = (i / 96) * Math.PI * 2 + r() * 0.05;
    const inner = S * (0.26 + r() * 0.1);
    const outer = S * (0.42 + r() * 0.08);
    const grad = g.createLinearGradient(
      cx + Math.cos(ang) * inner, cy + Math.sin(ang) * inner,
      cx + Math.cos(ang) * outer, cy + Math.sin(ang) * outer);
    grad.addColorStop(0, `rgba(255,255,255,${(0.25 + r() * 0.5).toFixed(3)})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    g.strokeStyle = grad;
    g.lineWidth = 1 + r() * 3.2;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(cx + Math.cos(ang) * inner, cy + Math.sin(ang) * inner);
    g.lineTo(cx + Math.cos(ang) * outer, cy + Math.sin(ang) * outer);
    g.stroke();
  }
  // the bright leading edge itself
  g.strokeStyle = "rgba(255,255,255,0.75)";
  g.lineWidth = S * 0.035;
  g.beginPath(); g.arc(cx, cy, S * 0.42, 0, Math.PI * 2); g.stroke();
  g.globalCompositeOperation = "source-over";
  ringTex = new THREE.CanvasTexture(c);
  ringTex.colorSpace = THREE.SRGBColorSpace;
  return ringTex;
}

let debrisTex: THREE.CanvasTexture | null = null;

/**
 * CHARRED CHUNK: cracked, sooted rubble with a few glowing hot seams. Box UVs,
 * so each face of a debris cube gets the whole tile — chunks are 0.35 m and
 * airborne for under a second, so per-face variation is not worth a UV atlas.
 * Replaces flat `#ff6a2a`, which read as orange dice.
 */
export function debrisTexture(): THREE.CanvasTexture {
  if (debrisTex) return debrisTex;
  const S = 64;
  const { c, g } = cvs(S, S);
  const r = rnd(0xd3b71);
  const grain = fbm(S, 4, 3, 0xbe4a1);
  const img = g.createImageData(S, S);
  const d = img.data;
  for (let i = 0; i < S * S; i++) {
    const v = grain[i];
    const lum = 26 + v * 46; // dark sooty rock
    const o = i * 4;
    d[o] = Math.round(lum * 1.12); d[o + 1] = Math.round(lum * 0.96); d[o + 2] = Math.round(lum * 0.9);
    d[o + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  // cracks
  g.strokeStyle = "rgba(10,8,8,0.85)";
  for (let i = 0; i < 9; i++) {
    g.lineWidth = 0.6 + r() * 1.2;
    let x = r() * S, y = r() * S;
    g.beginPath(); g.moveTo(x, y);
    for (let k = 0; k < 4; k++) { x += (r() - 0.5) * S * 0.5; y += (r() - 0.5) * S * 0.5; g.lineTo(x, y); }
    g.stroke();
  }
  // hot seams — still-burning rock, the reason debris reads as EXPLODED
  g.globalCompositeOperation = "lighter";
  for (let i = 0; i < 7; i++) {
    const x = r() * S, y = r() * S, rad = S * (0.05 + r() * 0.1);
    const gr = g.createRadialGradient(x, y, 0, x, y, rad);
    gr.addColorStop(0, "rgba(255,140,50,0.9)");
    gr.addColorStop(0.5, "rgba(210,60,20,0.4)");
    gr.addColorStop(1, "rgba(120,20,10,0)");
    g.fillStyle = gr;
    g.fillRect(x - rad, y - rad, rad * 2, rad * 2);
  }
  g.globalCompositeOperation = "source-over";
  debrisTex = new THREE.CanvasTexture(c);
  debrisTex.colorSpace = THREE.SRGBColorSpace;
  return debrisTex;
}

/**
 * Build all six under the LOAD SCREEN. Combat FX are by definition first used
 * mid-fight — the first kill, the first rocket, the first trigger pull — which
 * is the worst possible moment to run ~60 ms of canvas work. Idempotent; the
 * renderer calls this from `precompile()` alongside the other five prewarms.
 */
export function prewarmCombatTextures(): void {
  firePuffTexture();
  smokePuffTexture();
  emberTexture();
  muzzleFlashTexture();
  shockRingTexture();
  debrisTexture();
}
